import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlanningBoard, type PlanningSearch } from "@/features/planning/PlanningBoard";
import { getBoard, requestSync } from "@/features/planning/planning.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { requireAccess } from "@/lib/routeAccess";

const MONDAY = /^\d{4}-\d{2}-\d{2}$/;
const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);

// Planbord (docs/planning-board.md). Behind the right `planning` (Beheer › Rechten);
// the API enforces it.
export const Route = createFileRoute("/_shell/planning")({
  beforeLoad: requireAccess(["planning"]),
  validateSearch: (search: Record<string, unknown>): PlanningSearch => ({
    view: search.view === "staff" ? "staff" : undefined,
    week: str(search.week) && MONDAY.test(str(search.week)!) ? str(search.week) : undefined,
    from: str(search.from) && MONDAY.test(str(search.from)!) ? str(search.from) : undefined,
    q: str(search.q),
    lead: str(search.lead),
    signals: search.signals === "1" ? "1" : undefined,
    free: search.free === "1" ? "1" : undefined,
  }),
  component: PlanningPage,
});

function PlanningPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [syncError, setSyncError] = useState<string | null>(null);

  // The DataBridge syncs every quarter of an hour; while a requested sync is on
  // its way, look more often so the answer shows up by itself.
  const query = useQuery({
    queryKey: ["planning-board", search.from ?? ""],
    queryFn: () => getBoard(search.from),
    refetchInterval: (q) => (q.state.data?.pending.length ? 10_000 : 5 * 60_000),
  });

  function updateParams(updates: Record<string, string>) {
    void navigate({
      to: "/planning",
      search: (prev) => applyParamUpdates(prev as Record<string, string | undefined>, updates),
      replace: true,
    });
  }

  const sync = useMutation({
    mutationFn: (weekStart?: string) => requestSync(weekStart),
    onMutate: () => setSyncError(null),
    onSuccess: (_r, weekStart) => {
      if (weekStart) {
        // Load from that week on, and show it.
        const from = search.from && search.from < weekStart ? search.from : weekStart;
        updateParams({ from, week: weekStart });
      }
      void queryClient.invalidateQueries({ queryKey: ["planning-board"] });
    },
    onError: (err) => setSyncError(err instanceof Error ? err.message : String(err)),
  });

  return (
    <PlanningBoard
      data={query.data}
      isLoading={query.isLoading}
      error={query.error}
      search={search}
      updateParams={updateParams}
      onSyncNow={() => sync.mutate(undefined)}
      onFetchWeek={(monday) => sync.mutate(monday)}
      syncError={syncError}
      syncBusy={sync.isPending}
    />
  );
}
